import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import { OPPORTUNITY_STATUS_STYLES } from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';

// Centralized OpportunityStatus vocabulary. Existing ad hoc copies of this
// vocabulary (optimizeSummaryTab3.tsx, OpportunitySidebar.tsx,
// OpportunityBriefTab.tsx) are intentionally left as-is — only v3 consumes
// this module, to avoid touching files v1/v2 depend on.
export type OpportunityStatus =
  | 'Draft'
  | 'Pending Review'
  | 'Approved'
  | 'In Progress'
  | 'Verifying'
  | 'Savings Booked'
  | 'Rejected'
  | 'Deferred'
  | 'Cancelled';

export interface OpportunityStatusMeta {
  status: OpportunityStatus;
  // Narrative label used in funnel/summary copy (distinct from the compact
  // uppercase tag rendered by <OptimizeStatus />).
  label: string;
  // Position in the primary Draft -> Savings Booked funnel. Approved and In Progress
  // share a position — they're the same funnel stage. null = off the primary
  // funnel (Rejected, Deferred, Cancelled).
  funnelOrder: number | null;
  // Not yet in a terminal state.
  isActive: boolean;
  // Won't transition further (Savings Booked, Rejected, Cancelled).
  isTerminal: boolean;
}

export const OPPORTUNITY_STATUS_META: Record<
  OpportunityStatus,
  OpportunityStatusMeta
> = {
  Draft: {
    status: 'Draft',
    label: 'Draft · in preparation',
    funnelOrder: 0,
    isActive: true,
    isTerminal: false,
  },
  'Pending Review': {
    status: 'Pending Review',
    label: 'Awaiting your review',
    funnelOrder: 1,
    isActive: true,
    isTerminal: false,
  },
  Approved: {
    status: 'Approved',
    label: 'Approved · in progress',
    funnelOrder: 2,
    isActive: true,
    isTerminal: false,
  },
  'In Progress': {
    status: 'In Progress',
    label: 'Approved · in progress',
    funnelOrder: 2,
    isActive: true,
    isTerminal: false,
  },
  Verifying: {
    status: 'Verifying',
    label: 'Completed · verifying',
    funnelOrder: 3,
    isActive: true,
    isTerminal: false,
  },
  'Savings Booked': {
    status: 'Savings Booked',
    label: 'Savings realized',
    funnelOrder: 4,
    isActive: false,
    isTerminal: true,
  },
  Rejected: {
    status: 'Rejected',
    label: 'Rejected',
    funnelOrder: null,
    isActive: false,
    isTerminal: true,
  },
  Deferred: {
    status: 'Deferred',
    label: 'Deferred',
    funnelOrder: null,
    isActive: true,
    isTerminal: false,
  },
  Cancelled: {
    status: 'Cancelled',
    label: 'Cancelled',
    funnelOrder: null,
    isActive: false,
    isTerminal: true,
  },
};

const KNOWN_STATUSES = new Set<string>(Object.keys(OPPORTUNITY_STATUS_META));

const isKnownStatus = (value: string): value is OpportunityStatus =>
  KNOWN_STATUSES.has(value);

/**
 * Returns the opportunity's status, falling back to a value derived from
 * ticket status + approvalRequests for tickets that predate the
 * opportunityStatus field (ported from optimizeSummaryTab3.tsx).
 */
export const getOpportunityStatus = (
  rec: TicketQueryResult,
): OpportunityStatus => {
  const opportunityStatus = rec.ticket.extras?.optimize?.opportunityStatus;

  if (opportunityStatus && isKnownStatus(opportunityStatus)) {
    return opportunityStatus;
  }

  const hasApproval =
    rec.ticket.approvalRequests?.some((req) => req.status === 'APPROVED') ===
    true;

  switch (rec.ticket.status) {
    case 'OPEN':
      return hasApproval ? 'Approved' : 'Pending Review';
    case 'RESOLVED':
      return 'Verifying';
    case 'CLOSED':
      return rec.ticket.subStatus === 'CANCELLED'
        ? 'Cancelled'
        : 'Savings Booked';
    default:
      return 'Pending Review';
  }
};

export const getOpportunityStatusMeta = (
  rec: TicketQueryResult,
): OpportunityStatusMeta => OPPORTUNITY_STATUS_META[getOpportunityStatus(rec)];

// Reuses the same tag colors already shown elsewhere (OptimizeStatus tag,
// ticket detail Optimize panel) rather than introducing a second palette.
export const getOpportunityStatusColor = (status: OpportunityStatus): string =>
  OPPORTUNITY_STATUS_STYLES[status]?.color ?? '#595959';
